"use client";

import config from "@/configs/config";
import staticData from "@/public/data/components/sortOrder.json";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/common/Select";
import Typography from "@/shared/components/common/Typography";
import { usePathname } from "next/navigation";
import Flex from "../../../common/Flex";
import List from "../../../common/List";
import {
  replaceClientSearchParams,
  useClientSearchParams,
} from "@/shared/hooks/useClientSearchParams";
import { useDeviceType } from "@/shared/context/DeviceContext";
import { Button } from "../../../common/button";
import styles from "./sortFilter.module.css";
import type { SortFilterItem } from "../../filter.types";
import { cn } from "@/shared/libs/commonUtils";

interface SortFilterProps {
  filters: SortFilterItem[];
}

export default function SortFilter({ filters }: SortFilterProps) {
  const defaultValue = filters[0].label;
  const pathName = usePathname();
  const params = useClientSearchParams();
  const deviceType = useDeviceType();

  const currentSort = filters.find(
    ({ name }) => name === params.get("sort")
  )?.label;

  const handleSortChange = (value: string) => {
    const searchParams = new URLSearchParams(params);
    searchParams.set("sort", value);
    replaceClientSearchParams(pathName, searchParams);
  };

  const handleSortChangeMobile = (value: string) => () => {
    handleSortChange(value);
  };

  return deviceType === "desktop" ? (
    <Select dir={config.dir} onValueChange={handleSortChange}>
      <SelectTrigger className="w-full bg-background ring-1 ring-border border-none rounded-lg min-h-14 shadow-none relative">
        <Flex className="flex-col gap-1 justify-start px-8">
          <Typography
            variant="caption"
            className="text-xs text-muted-foreground text-justify"
          >
            {staticData.title}
          </Typography>
          <SelectValue placeholder={currentSort || defaultValue} />
        </Flex>
      </SelectTrigger>
      <SelectContent className="rounded-lg">
        <List
          items={filters}
          renderItems={({ name, label }) => (
            <SelectItem className="rounded-xs p-2" value={name}>
              <Typography variant="body2">{label}</Typography>
            </SelectItem>
          )}
        />
      </SelectContent>
    </Select>
  ) : (
    <Flex
      className={`${styles.hide_scrollbar} w-full overflow-x-auto gap-x-1 px-1 `}
    >
      <List
        items={filters}
        renderItems={({ name, label }) => (
          <Button
            variant="outline"
            onClick={handleSortChangeMobile(name)}
            className={cn(
              "!rounded-full !py-0 !h-9 !px-3  transition-all duration-200 ease-out",
              currentSort === name &&
                "!border-primary !text-primary !bg-primary-10 scale-x-105"
            )}
          >
            {label}
          </Button>
        )}
      />
    </Flex>
  );
}
